// src/view/RegistrarSegPracticaMasivo.jsx

import axios from "axios";
import Swal from "sweetalert2";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/* =================== ENUMS =================== */

const ENUMS = {
  modalidadSesion: ["Presencial", "Virtual"],
  modalidadSeguimiento: ["Presencial", "Virtual", "A Distancia", "Híbrida"],
  formaEvaluacion: ["Virtual", "Presencial"],
  valoracion: ["Satisfactorio", "Por mejorar"],
  juicioEvaluacion: ["Aprobado", "No aprobado"],

  factoresTecnicos: [
    "Aplicación de conocimiento",
    "Mejora continua",
    "Fortalecimiento ocupacional",
    "Oportunidad y calidad",
    "Responsabilidad ambiental",
    "Administración de recursos",
    "Seguridad y salud en el trabajo",
    "Documentación etapa productiva",
  ],

  factoresActitudinales: [
    "Relaciones interpersonales",
    "Trabajo en equipo",
    "Solución de problemas",
    "Cumplimiento",
    "Organización",
  ],
};

/* =================== ESTADOS INICIALES =================== */

const initialForm = {
  // Momento 1
  m1_ciudad: "",
  m1_fechaDiligenciamiento: "",
  m1_modalidadSesion: "",

  // Momento 2
  m2_fechaMomentoSeguimiento: "",
  m2_modalidadSeguimiento: "",
  m2_enlaceGrabacion: "",
  m2_obs_ComplementInst: "",
  m2_obs_Aprendiz: "",
  m2_obs_EnteCoformador: "",
  m2_ciudad: "",
  m2_fechaDiligenciamiento: "",
  m2_modalidadSesion: "",

  // Momento 3
  m3_numeroVisitasRealizadas: "",
  m3_formaEvaluacion: "",
  m3_enlaceGrabacion: "",
  m3_retroEnte_proceso: "",
  m3_retroInstructor_proceso: "",
  m3_retroAprendiz_proceso: "",
  m3_retroEnte_desempeno: "",
  m3_retroInstructor_desempeno: "",
  m3_retroAprendiz_desempeno: "",
  m3_juicioEvaluacion: "",
  m3_ciudad: "",
  m3_fechaDiligenciamiento: "",
  m3_modalidadSesion: "",
};

const initialListas = {
  m1_competenciasADesarrollar: [],
  m1_resultadosAprendizaje: [],
  m1_actividadesADesarrollar: [],
  m1_evidenciasAprendizaje: [],
  m1_observacionesAdicionales: [],
};

const initialFactores = {
  m2_factoresTecnicos: [],
  m2_factoresActitudinales: [],
  m3_factoresTecnicos: [],
  m3_factoresActitudinales: [],
};

/* =================== CONFIGURACIONES =================== */

const LIST_CONFIG = [
  {
    key: "m1_competenciasADesarrollar",
    title: "Competencias a desarrollar",
    placeholder: "Competencia…",
    icon: "fa-solid fa-layer-group",
    tone: "primary",
  },
  {
    key: "m1_resultadosAprendizaje",
    title: "Resultados de aprendizaje",
    placeholder: "Resultado…",
    icon: "fa-solid fa-bullseye",
    tone: "success",
  },
  {
    key: "m1_actividadesADesarrollar",
    title: "Actividades a desarrollar",
    placeholder: "Actividad…",
    icon: "fa-solid fa-list-check",
    tone: "info",
  },
  {
    key: "m1_evidenciasAprendizaje",
    title: "Evidencias de aprendizaje",
    placeholder: "Evidencia…",
    icon: "fa-regular fa-file-lines",
    tone: "warning",
  },
  {
    key: "m1_observacionesAdicionales",
    title: "Observaciones adicionales",
    placeholder: "Observación…",
    icon: "fa-regular fa-comments",
    tone: "secondary",
  },
];

const FACTOR_CONFIG = [
  {
    key: "m2_factoresTecnicos",
    title: "Momento 2 — Factores técnicos",
    cardTitle: "Factores técnicos",
    variables: ENUMS.factoresTecnicos,
    icon: "fa-solid fa-microscope",
    tone: "primary",
  },
  {
    key: "m2_factoresActitudinales",
    title: "Momento 2 — Factores actitudinales",
    cardTitle: "Factores actitudinales",
    variables: ENUMS.factoresActitudinales,
    icon: "fa-solid fa-users-gear",
    tone: "success",
  },
  {
    key: "m3_factoresTecnicos",
    title: "Momento 3 — Factores técnicos",
    cardTitle: "Factores técnicos",
    variables: ENUMS.factoresTecnicos,
    icon: "fa-solid fa-microscope",
    tone: "primary",
  },
  {
    key: "m3_factoresActitudinales",
    title: "Momento 3 — Factores actitudinales",
    cardTitle: "Factores actitudinales",
    variables: ENUMS.factoresActitudinales,
    icon: "fa-solid fa-users-gear",
    tone: "success",
  },
];

/* =================== HELPERS =================== */

const limpiarTexto = (value = "") => String(value || "").trim();

const limpiarArrayTexto = (array = []) => {
  return (Array.isArray(array) ? array : [])
    .map((item) => limpiarTexto(item))
    .filter(Boolean);
};

const getToken = () =>
  localStorage.getItem("token") || sessionStorage.getItem("token") || "";

const getHeaders = () => {
  const token = getToken();

  return token
    ? {
      Authorization: `Bearer ${token}`,
    }
    : {};
};

const mapByVariable = (array = []) => {
  return (Array.isArray(array) ? array : []).reduce((acc, item) => {
    if (item?.variable) {
      acc[item.variable] = {
        valoracion: item.valoracion || "",
        observaciones: item.observaciones || "",
      };
    }

    return acc;
  }, {});
};

const factorMapToArray = (map = {}) => {
  return Object.entries(map)
    .map(([variable, item]) => ({
      variable,
      valoracion: item?.valoracion || "",
      observaciones: item?.observaciones || "",
    }))
    .filter((item) => item.valoracion || item.observaciones);
};

const actualizarFactor = (arrayActual = [], variable, campo, valor) => {
  const map = mapByVariable(arrayActual);

  map[variable] = {
    ...(map[variable] || {}),
    [campo]: valor,
  };

  return factorMapToArray(map);
};

/* =================== COMPONENTES UI =================== */

const IconBubble = ({ icon, tone = "primary" }) => {
  const toneMap = {
    primary: "bg-primary-subtle text-primary",
    success: "bg-success-subtle text-success",
    info: "bg-info-subtle text-info",
    warning: "bg-warning-subtle text-warning",
    secondary: "bg-secondary-subtle text-secondary",
    dark: "bg-dark-subtle text-dark",
  };

  return (
    <div
      className={`rounded-circle d-flex align-items-center justify-content-center ${toneMap[tone] || toneMap.primary
        }`}
      style={{
        width: 44,
        height: 44,
        minWidth: 44,
      }}
    >
      <i className={icon}></i>
    </div>
  );
};

const SectionCard = ({
  icon,
  tone = "primary",
  title,
  subtitle,
  action,
  children,
}) => (
  <div className="card border-0 shadow-sm h-100 rounded-4">
    <div className="card-body py-3">
      <div className="d-flex align-items-start gap-3">
        <IconBubble icon={icon} tone={tone} />

        <div className="w-100">
          <div className="d-flex justify-content-between align-items-center gap-2">
            <div className="fw-semibold">{title}</div>
            {action}
          </div>

          {subtitle && <div className="small text-muted mt-1">{subtitle}</div>}

          {children}
        </div>
      </div>
    </div>
  </div>
);

const Pill = ({ children }) => (
  <span className="badge rounded-pill text-bg-light border">{children}</span>
);

const EmptyState = ({ subtitle = "Aún no hay información." }) => (
  <div className="text-center text-muted py-4">
    <div className="display-6 mb-2">🗂️</div>
    <div className="fw-semibold">Sin registros</div>
    <div className="small">{subtitle}</div>
  </div>
);

const InputTexto = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  col = "col-12 col-md-4",
}) => (
  <div className={col}>
    <label className="form-label small fw-semibold">{label}</label>

    <input
      name={name}
      type={type}
      className="form-control"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
    />
  </div>
);

const TextArea = ({
  label,
  name,
  value,
  onChange,
  placeholder = "",
  col = "col-12 col-md-4",
}) => (
  <div className={col}>
    <label className="form-label small fw-semibold">{label}</label>

    <textarea
      name={name}
      className="form-control"
      rows={3}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
    />
  </div>
);

const SelectSimple = ({
  label,
  name,
  value,
  onChange,
  options = [],
  col = "col-12 col-md-4",
}) => (
  <div className={col}>
    <label className="form-label small fw-semibold">{label}</label>

    <select
      name={name}
      className="form-select"
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
    >
      <option value="">Seleccione…</option>

      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </div>
);

/* =================== MODALES =================== */

const ArrayEditorModal = ({
  show,
  onClose,
  title,
  placeholder,
  items = [],
  onAdd,
  onRemove,
  onEdit,
}) => {
  const [value, setValue] = useState("");
  const [editIndex, setEditIndex] = useState(-1);
  const [editValue, setEditValue] = useState("");

  if (!show) return null;

  const limpiarModal = () => {
    setValue("");
    setEditIndex(-1);
    setEditValue("");
  };

  const handleClose = () => {
    limpiarModal();
    onClose();
  };

  const handleAdd = () => {
    const text = limpiarTexto(value);

    if (!text) return;

    onAdd(text);
    setValue("");
  };

  const startEdit = (index, currentValue) => {
    setEditIndex(index);
    setEditValue(currentValue);
  };

  const cancelEdit = () => {
    setEditIndex(-1);
    setEditValue("");
  };

  const saveEdit = () => {
    const newValue = limpiarTexto(editValue);

    if (!newValue) return;
    if (editIndex < 0) return;

    onEdit(editIndex, newValue);
    cancelEdit();
  };

  return (
    <>
      <div
        className="modal show"
        style={{
          display: "block",
        }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content shadow border-0 rounded-4">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title fw-bold">{title}</h5>
                <div className="small text-muted">
                  Agrega, edita o elimina elementos para guardar masivamente.
                </div>
              </div>

              <button type="button" className="btn-close" onClick={handleClose} />
            </div>

            <div className="modal-body">
              {items.length === 0 ? (
                <EmptyState subtitle="Agrega el primer elemento con el campo inferior." />
              ) : (
                <div className="list-group rounded-3 border mb-3">
                  {items.map((item, index) => (
                    <div
                      key={`${item}-${index}`}
                      className="list-group-item d-flex justify-content-between gap-3"
                    >
                      <div className="w-100">
                        {editIndex === index ? (
                          <textarea
                            className="form-control form-control-sm"
                            value={editValue}
                            rows={2}
                            onChange={(e) => setEditValue(e.target.value)}
                          />
                        ) : (
                          <div
                            style={{
                              wordBreak: "break-word",
                            }}
                          >
                            {item}
                          </div>
                        )}
                      </div>

                      <div className="d-flex gap-2">
                        {editIndex === index ? (
                          <>
                            <button
                              className="btn btn-sm btn-success"
                              type="button"
                              onClick={saveEdit}
                              title="Guardar"
                            >
                              <i className="fa-solid fa-check"></i>
                            </button>

                            <button
                              className="btn btn-sm btn-outline-secondary"
                              type="button"
                              onClick={cancelEdit}
                              title="Cancelar"
                            >
                              <i className="fa-solid fa-rotate-left"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              type="button"
                              onClick={() => startEdit(index, item)}
                              title="Editar"
                            >
                              <i className="fa-solid fa-pen"></i>
                            </button>

                            <button
                              className="btn btn-sm btn-outline-danger"
                              type="button"
                              onClick={() => onRemove(index)}
                              title="Eliminar"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <label className="form-label small fw-semibold">Nuevo elemento</label>

              <div className="input-group">
                <input
                  className="form-control"
                  placeholder={placeholder || "Texto…"}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAdd();
                    }
                  }}
                />

                <button className="btn btn-success" type="button" onClick={handleAdd}>
                  <i className="fa-solid fa-plus me-1"></i>
                  Añadir
                </button>
              </div>
            </div>

            <div className="modal-footer border-0">
              <button className="btn btn-secondary" type="button" onClick={handleClose}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop show" />
    </>
  );
};

const FactoresTableModal = ({
  show,
  onClose,
  title,
  variables = [],
  valoracionEnum = [],
  values = {},
  onSet,
}) => {
  if (!show) return null;

  return (
    <>
      <div
        className="modal show"
        style={{
          display: "block",
        }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 rounded-4">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title mb-1">{title}</h5>
                <div className="small text-muted">{variables.length} variables</div>
              </div>

              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body pt-3">
              <div className="table-responsive rounded-3 border">
                <table className="table table-sm table-striped align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Variable</th>
                      <th style={{ width: 220 }}>Valoración</th>
                      <th>Observaciones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {variables.map((variable) => (
                      <tr key={variable}>
                        <td className="fw-semibold">{variable}</td>

                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={values?.[variable]?.valoracion || ""}
                            onChange={(e) =>
                              onSet(variable, "valoracion", e.target.value)
                            }
                          >
                            <option value="">Seleccione…</option>

                            {valoracionEnum.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <input
                            className="form-control form-control-sm"
                            placeholder="Observaciones"
                            value={values?.[variable]?.observaciones || ""}
                            onChange={(e) =>
                              onSet(variable, "observaciones", e.target.value)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer border-0">
              <button className="btn btn-secondary" type="button" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop show" />
    </>
  );
};

/* =================== COMPONENTE PRINCIPAL =================== */

const RegistrarSegPracticaMasivo = () => {
  const navigate = useNavigate();
  const { id, matriculaId, etapaId } = useParams();

  const matriculaDestino = matriculaId || id;

  const [form, setForm] = useState(initialForm);
  const [listas, setListas] = useState(initialListas);
  const [factores, setFactores] = useState(initialFactores);

  const [saving, setSaving] = useState(false);
  const [modalLista, setModalLista] = useState(null);
  const [modalFactor, setModalFactor] = useState(null);

  const activeLista = LIST_CONFIG.find((item) => item.key === modalLista);
  const activeFactor = FACTOR_CONFIG.find((item) => item.key === modalFactor);

  const m2TecValues = useMemo(
    () => mapByVariable(factores.m2_factoresTecnicos),
    [factores.m2_factoresTecnicos]
  );

  const m2ActValues = useMemo(
    () => mapByVariable(factores.m2_factoresActitudinales),
    [factores.m2_factoresActitudinales]
  );

  const m3TecValues = useMemo(
    () => mapByVariable(factores.m3_factoresTecnicos),
    [factores.m3_factoresTecnicos]
  );

  const m3ActValues = useMemo(
    () => mapByVariable(factores.m3_factoresActitudinales),
    [factores.m3_factoresActitudinales]
  );

  const getFactorValues = (key) => {
    if (key === "m2_factoresTecnicos") return m2TecValues;
    if (key === "m2_factoresActitudinales") return m2ActValues;
    if (key === "m3_factoresTecnicos") return m3TecValues;
    if (key === "m3_factoresActitudinales") return m3ActValues;

    return {};
  };

  const setCampo = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addListaItem = (field, value) => {
    const text = limpiarTexto(value);

    if (!text) return;

    setListas((prev) => ({
      ...prev,
      [field]: limpiarArrayTexto([...(prev[field] || []), text]),
    }));
  };

  const removeListaItem = (field, indexToRemove) => {
    setListas((prev) => ({
      ...prev,
      [field]: (prev[field] || []).filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  const editListaItem = (field, indexToEdit, newValue) => {
    const text = limpiarTexto(newValue);

    if (!text) return;

    setListas((prev) => ({
      ...prev,
      [field]: (prev[field] || []).map((item, index) =>
        index === indexToEdit ? text : item
      ),
    }));
  };

  const setFactor = (fieldName, variable, field, value) => {
    setFactores((prev) => ({
      ...prev,
      [fieldName]: actualizarFactor(prev[fieldName], variable, field, value),
    }));
  };

  const cleanData = () => {
    setForm({ ...initialForm });
    setListas({ ...initialListas });
    setFactores({ ...initialFactores });
    setModalLista(null);
    setModalFactor(null);
  };

  const buildDefaults = () => {
    const defaults = {};

    Object.entries(form).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        defaults[key] =
          key === "m3_numeroVisitasRealizadas" ? Number(value) : value;
      }
    });

    Object.entries(listas).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        defaults[key] = value;
      }
    });

    Object.entries(factores).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        defaults[key] = value;
      }
    });

    return defaults;
  };

  const saveSegMasivo = async (defaults = {}) => {
    const matriculaIdFinal = matriculaDestino;

    if (!matriculaIdFinal) {
      await Swal.fire({
        icon: "warning",
        title: "Matrícula requerida",
        text: "No se recibió el ID de la matrícula.",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!defaults || Object.keys(defaults).length === 0) {
      await Swal.fire({
        icon: "warning",
        title: "Sin información",
        text: "No hay datos para guardar de forma masiva.",
        confirmButtonText: "OK",
      });
      return;
    }

    const payload = {
      etapaPracticaObjectId: etapaId || null,
      soloActivos: true,
      skipWhenNoEtapa: false,
      dryRun: false,
      bloquearExistentes: true,
      defaults,
    };

    try {
      setSaving(true);

      const { data } = await axios.post(
        `/api/seguimiento/bulk/${matriculaIdFinal}`,
        payload,
        {
          headers: getHeaders(),
        }
      );

      const totalAprendices = data?.summary?.totalAprendices || 0;
      const created = data?.summary?.created || 0;
      const updated = data?.summary?.updated || 0;
      const matched = data?.summary?.matched || 0;
      const skipped = data?.summary?.skipped || 0;

      await Swal.fire({
        icon: "success",
        title: "Guardado masivo completado",
        html: `
          <div class="text-start">
            <div>
              ${data?.message || "Seguimientos procesados correctamente."}
            </div>

            <hr>

            <div><b>Total aprendices:</b> ${totalAprendices}</div>
            <div><b>Creados:</b> ${created}</div>
            <div><b>Actualizados:</b> ${updated}</div>
            <div><b>Coincidencias:</b> ${matched}</div>
            <div><b>Omitidos:</b> ${skipped}</div>
          </div>
        `,
        confirmButtonText: "OK",
      });

      cleanData();
    } catch (error) {
      console.error(
        "Error en saveSegMasivo:",
        error.response?.data || error.message
      );

      if (error?.response?.status === 409) {
        await Swal.fire({
          icon: "warning",
          title: "Registro masivo bloqueado",
          text:
            error.response?.data?.message ||
            "Ya existen seguimientos para esta matrícula.",
          confirmButtonText: "OK",
        });

        cleanData();
        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Error al guardar masivamente",
        text:
          error.response?.data?.message ||
          "No se pudo guardar la información de forma masiva.",
        confirmButtonText: "OK",
      });
    } finally {
      setSaving(false);
    }
  };

  const actions = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (saving) return;

    const defaults = buildDefaults();

    await saveSegMasivo(defaults);
  };

  const handleCancelOrClean = () => {
    cleanData();
  };

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h4 className="mb-1 fw-semibold">
                Registro Masivo de Seguimiento
              </h4>

              <p className="mb-0 text-secondary small">
                Diligencia los datos que serán aplicados a todos los aprendices
                de la matrícula.
              </p>

              <div className="small text-muted mt-2">
                <b>Matrícula:</b> {matriculaDestino || "No definida"}
                {etapaId && (
                  <>
                    {" "}
                    · <b>Etapa:</b> {etapaId}
                  </>
                )}
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={() => navigate(-1)}
                disabled={saving}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleCancelOrClean}
                disabled={saving}
              >
                <i className="fa-solid fa-eraser me-2"></i>
                Limpiar
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={actions}>
          <div className="card shadow-sm border-0 rounded-4">
            <div className="card-body p-0">
              <div className="tabs-wrap px-3 pt-3">
                <ul className="nav nav-tabs" role="tablist">
                  <li className="nav-item">
                    <button
                      className="nav-link active"
                      data-bs-toggle="tab"
                      data-bs-target="#m1"
                      type="button"
                    >
                      <i className="fa-regular fa-compass me-2"></i>
                      Momento 1 · Planeación
                    </button>
                  </li>

                  <li className="nav-item">
                    <button
                      className="nav-link"
                      data-bs-toggle="tab"
                      data-bs-target="#m2"
                      type="button"
                    >
                      <i className="fa-solid fa-chart-line me-2"></i>
                      Momento 2 · Seguimiento
                    </button>
                  </li>

                  <li className="nav-item">
                    <button
                      className="nav-link"
                      data-bs-toggle="tab"
                      data-bs-target="#m3"
                      type="button"
                    >
                      <i className="fa-solid fa-clipboard-check me-2"></i>
                      Momento 3 · Evaluación
                    </button>
                  </li>
                </ul>
              </div>

              <div className="tab-content p-3 p-md-4">
                {/* ================= MOMENTO 1 ================= */}

                <div className="tab-pane fade show active" id="m1">
                  <div className="row g-3">
                    {LIST_CONFIG.map((item) => (
                      <div
                        className={
                          item.key === "m1_observacionesAdicionales"
                            ? "col-12"
                            : "col-12 col-lg-6"
                        }
                        key={item.key}
                      >
                        <SectionCard
                          icon={item.icon}
                          tone={item.tone}
                          title={item.title}
                          subtitle="Ver y editar dentro del modal"
                          action={
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              type="button"
                              onClick={() => setModalLista(item.key)}
                            >
                              <i className="fa-solid fa-table-list me-1"></i>
                              Abrir
                            </button>
                          }
                        >
                          <div className="mt-2 d-flex align-items-center justify-content-between">
                            <div className="small text-muted">
                              Elementos: {listas[item.key]?.length || 0}
                            </div>

                            <Pill>
                              {listas[item.key]?.length
                                ? "Cargado"
                                : "Por definir"}
                            </Pill>
                          </div>
                        </SectionCard>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4">
                    <h6 className="fw-semibold mb-2">
                      <i className="fa-regular fa-pen-to-square me-2 text-primary"></i>
                      Firmas
                    </h6>

                    <div className="row g-3">
                      <InputTexto
                        label="Ciudad"
                        name="m1_ciudad"
                        value={form.m1_ciudad}
                        onChange={setCampo}
                        placeholder="Ciudad"
                      />

                      <InputTexto
                        label="Fecha de diligenciamiento"
                        name="m1_fechaDiligenciamiento"
                        type="date"
                        value={form.m1_fechaDiligenciamiento}
                        onChange={setCampo}
                      />

                      <SelectSimple
                        label="Modalidad de sesión"
                        name="m1_modalidadSesion"
                        value={form.m1_modalidadSesion}
                        onChange={setCampo}
                        options={ENUMS.modalidadSesion}
                      />
                    </div>
                  </div>
                </div>

                {/* ================= MOMENTO 2 ================= */}

                <div className="tab-pane fade" id="m2">
                  <div className="row g-3">
                    <InputTexto
                      label="Fecha del seguimiento"
                      name="m2_fechaMomentoSeguimiento"
                      type="date"
                      value={form.m2_fechaMomentoSeguimiento}
                      onChange={setCampo}
                    />

                    <SelectSimple
                      label="Modalidad"
                      name="m2_modalidadSeguimiento"
                      value={form.m2_modalidadSeguimiento}
                      onChange={setCampo}
                      options={ENUMS.modalidadSeguimiento}
                    />

                    <InputTexto
                      label="Enlace de grabación"
                      name="m2_enlaceGrabacion"
                      type="url"
                      value={form.m2_enlaceGrabacion}
                      onChange={setCampo}
                      placeholder="https://..."
                    />
                  </div>

                  <div className="row g-3 mt-1">
                    {FACTOR_CONFIG.filter((item) =>
                      item.key.startsWith("m2")
                    ).map((item) => (
                      <div className="col-12 col-lg-6" key={item.key}>
                        <SectionCard
                          icon={item.icon}
                          tone={item.tone}
                          title={item.cardTitle}
                          subtitle="Ver y editar dentro del modal"
                          action={
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              type="button"
                              onClick={() => setModalFactor(item.key)}
                            >
                              <i className="fa-solid fa-table-list me-1"></i>
                              Abrir
                            </button>
                          }
                        >
                          <div className="mt-2 d-flex align-items-center justify-content-between">
                            <div className="small text-muted">
                              Variables: {item.variables.length}
                            </div>

                            <Pill>
                              {factores[item.key]?.length
                                ? "Cargado"
                                : "Por definir"}
                            </Pill>
                          </div>
                        </SectionCard>
                      </div>
                    ))}
                  </div>

                  <div className="row g-3 mt-1">
                    <TextArea
                      label="Complemento Instructor"
                      name="m2_obs_ComplementInst"
                      value={form.m2_obs_ComplementInst}
                      onChange={setCampo}
                      placeholder="Observaciones del instructor"
                    />

                    <TextArea
                      label="Aprendiz"
                      name="m2_obs_Aprendiz"
                      value={form.m2_obs_Aprendiz}
                      onChange={setCampo}
                      placeholder="Observaciones del aprendiz"
                    />

                    <TextArea
                      label="Ente coformador"
                      name="m2_obs_EnteCoformador"
                      value={form.m2_obs_EnteCoformador}
                      onChange={setCampo}
                      placeholder="Observaciones del ente"
                    />
                  </div>

                  <div className="mt-4">
                    <h6 className="fw-semibold mb-2">
                      <i className="fa-regular fa-pen-to-square me-2 text-primary"></i>
                      Firmas
                    </h6>

                    <div className="row g-3">
                      <InputTexto
                        label="Ciudad"
                        name="m2_ciudad"
                        value={form.m2_ciudad}
                        onChange={setCampo}
                        placeholder="Ciudad"
                      />

                      <InputTexto
                        label="Fecha de diligenciamiento"
                        name="m2_fechaDiligenciamiento"
                        type="date"
                        value={form.m2_fechaDiligenciamiento}
                        onChange={setCampo}
                      />

                      <SelectSimple
                        label="Modalidad de sesión"
                        name="m2_modalidadSesion"
                        value={form.m2_modalidadSesion}
                        onChange={setCampo}
                        options={ENUMS.modalidadSesion}
                      />
                    </div>
                  </div>
                </div>

                {/* ================= MOMENTO 3 ================= */}

                <div className="tab-pane fade" id="m3">
                  <div className="row g-3">
                    <InputTexto
                      label="# Visitas realizadas"
                      name="m3_numeroVisitasRealizadas"
                      type="number"
                      value={form.m3_numeroVisitasRealizadas}
                      onChange={setCampo}
                      placeholder="0"
                    />

                    <SelectSimple
                      label="Forma de evaluación"
                      name="m3_formaEvaluacion"
                      value={form.m3_formaEvaluacion}
                      onChange={setCampo}
                      options={ENUMS.formaEvaluacion}
                    />

                    <InputTexto
                      label="Enlace de grabación"
                      name="m3_enlaceGrabacion"
                      type="url"
                      value={form.m3_enlaceGrabacion}
                      onChange={setCampo}
                      placeholder="https://..."
                    />
                  </div>

                  <div className="row g-3 mt-1">
                    {FACTOR_CONFIG.filter((item) =>
                      item.key.startsWith("m3")
                    ).map((item) => (
                      <div className="col-12 col-lg-6" key={item.key}>
                        <SectionCard
                          icon={item.icon}
                          tone={item.tone}
                          title={item.cardTitle}
                          subtitle="Ver y editar dentro del modal"
                          action={
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              type="button"
                              onClick={() => setModalFactor(item.key)}
                            >
                              <i className="fa-solid fa-table-list me-1"></i>
                              Abrir
                            </button>
                          }
                        >
                          <div className="mt-2 d-flex align-items-center justify-content-between">
                            <div className="small text-muted">
                              Variables: {item.variables.length}
                            </div>

                            <Pill>
                              {factores[item.key]?.length
                                ? "Cargado"
                                : "Por definir"}
                            </Pill>
                          </div>
                        </SectionCard>
                      </div>
                    ))}
                  </div>

                  <div className="row g-3 mt-1">
                    <TextArea
                      label="Ente — Proceso de formación"
                      name="m3_retroEnte_proceso"
                      value={form.m3_retroEnte_proceso}
                      onChange={setCampo}
                      placeholder="Proceso de formación"
                    />

                    <TextArea
                      label="Instructor — Proceso de formación"
                      name="m3_retroInstructor_proceso"
                      value={form.m3_retroInstructor_proceso}
                      onChange={setCampo}
                      placeholder="Proceso de formación"
                    />

                    <TextArea
                      label="Aprendiz — Proceso de formación"
                      name="m3_retroAprendiz_proceso"
                      value={form.m3_retroAprendiz_proceso}
                      onChange={setCampo}
                      placeholder="Proceso de formación"
                    />

                    <TextArea
                      label="Ente — Desempeño de competencias"
                      name="m3_retroEnte_desempeno"
                      value={form.m3_retroEnte_desempeno}
                      onChange={setCampo}
                      placeholder="Desempeño de competencias"
                    />

                    <TextArea
                      label="Instructor — Desempeño de competencias"
                      name="m3_retroInstructor_desempeno"
                      value={form.m3_retroInstructor_desempeno}
                      onChange={setCampo}
                      placeholder="Desempeño de competencias"
                    />

                    <TextArea
                      label="Aprendiz — Desempeño de competencias"
                      name="m3_retroAprendiz_desempeno"
                      value={form.m3_retroAprendiz_desempeno}
                      onChange={setCampo}
                      placeholder="Desempeño de competencias"
                    />
                  </div>

                  <div className="mt-4">
                    <h6 className="fw-semibold mb-2">
                      <i className="fa-regular fa-pen-to-square me-2 text-primary"></i>
                      Evaluación y firmas
                    </h6>

                    <div className="row g-3">
                      <SelectSimple
                        label="Juicio de evaluación"
                        name="m3_juicioEvaluacion"
                        value={form.m3_juicioEvaluacion}
                        onChange={setCampo}
                        options={ENUMS.juicioEvaluacion}
                        col="col-12 col-md-3"
                      />

                      <InputTexto
                        label="Ciudad"
                        name="m3_ciudad"
                        value={form.m3_ciudad}
                        onChange={setCampo}
                        placeholder="Ciudad"
                        col="col-12 col-md-3"
                      />

                      <InputTexto
                        label="Fecha de diligenciamiento"
                        name="m3_fechaDiligenciamiento"
                        type="date"
                        value={form.m3_fechaDiligenciamiento}
                        onChange={setCampo}
                        col="col-12 col-md-3"
                      />

                      <SelectSimple
                        label="Modalidad de sesión"
                        name="m3_modalidadSesion"
                        value={form.m3_modalidadSesion}
                        onChange={setCampo}
                        options={ENUMS.modalidadSesion}
                        col="col-12 col-md-3"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mt-4">
            <div className="col-12 col-md-6">
              <button
                type="submit"
                className="btn btn-dark w-100 fw-semibold py-2"
                disabled={saving}
              >
                Guardar Masivo
              </button>
            </div>

            <div className="col-12 col-md-6">
              <button
                type="button"
                className="btn btn-outline-secondary w-100 fw-semibold py-2"
                onClick={handleCancelOrClean}
                disabled={saving}
              >
                Limpiar
              </button>
            </div>
          </div>
        </form>
      </div>

      <ArrayEditorModal
        show={!!activeLista}
        onClose={() => setModalLista(null)}
        title={activeLista?.title || ""}
        placeholder={activeLista?.placeholder || ""}
        items={activeLista ? listas[activeLista.key] : []}
        onAdd={(value) => activeLista && addListaItem(activeLista.key, value)}
        onRemove={(index) =>
          activeLista && removeListaItem(activeLista.key, index)
        }
        onEdit={(index, newValue) =>
          activeLista && editListaItem(activeLista.key, index, newValue)
        }
      />

      <FactoresTableModal
        show={!!activeFactor}
        onClose={() => setModalFactor(null)}
        title={activeFactor?.title || ""}
        variables={activeFactor?.variables || []}
        valoracionEnum={ENUMS.valoracion}
        values={activeFactor ? getFactorValues(activeFactor.key) : {}}
        onSet={(variable, field, value) =>
          activeFactor && setFactor(activeFactor.key, variable, field, value)
        }
      />
    </div>
  );
};

export default RegistrarSegPracticaMasivo;